import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ApiRequestError } from '../lib/api-client';
import { Modal } from '../ui/Modal';
import { qualifyContact } from './detail-api';
import { listCatalogue } from '../settings/catalogues-api';

/**
 * "This contact might buy something" (Phase 147).
 *
 * The owner's question was whether a contact should *become* a lead, and the answer the market
 * converged on is no: the person is already known, and a second copy of them is a duplicate
 * waiting to happen. What is new is the **attempt** — one sales conversation, with its own status,
 * its own dates and its own reason for ending — which is the thing a qualification rate counts.
 *
 * So this button raises an attempt, moves the contact to the Lead stage, and hands the agent to
 * the lead where the qualification actually happens.
 */

export function QualifyButton({
  contactId,
  contactName,
  compact,
}: {
  contactId: string;
  contactName: string;
  compact?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<string | null>(null);

  /*
   * The origins the tenant configured, not free text (Phase 147).
   *
   * A typed origin is how a source report turns into "indicação", "Indicação" and "indicacao" as
   * three channels — and the table that decides where the budget goes is exactly the one that
   * must not be split by somebody's capital letter. The list lives in Configurações › Cadastros;
   * this reads it.
   */
  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
    enabled: open,
  });

  const active = (sources.data ?? []).filter((entry) => entry.is_active !== false);

  const qualify = useMutation({
    mutationFn: () =>
      qualifyContact(contactId, {
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(sourceId ? { sourceId } : {}),
        ...(value.trim()
          ? { dealValueCents: Math.round(Number(value.replace(',', '.')) * 100) }
          : {}),
      }),
    onSuccess: (lead) => {
      setOpen(false);
      void client.invalidateQueries({ queryKey: ['crm-record', 'contacts', contactId] });
      void client.invalidateQueries({ queryKey: ['lead-funnel'] });
      navigate(`/crm/registro/leads/${lead.id}`);
    },
    onError: (caught: unknown) => {
      /*
       * One open attempt per person, and the refusal is useful rather than a wall.
       *
       * Two live leads about the same contact is two reps working them without knowing — so the
       * answer is a link to the one that already exists, not "não pode".
       */
      if (caught instanceof ApiRequestError && caught.code === 'crm.lead_already_open') {
        const leadId = (caught.payload as { leadId?: string } | undefined)?.leadId ?? null;

        setExisting(leadId);
        setError(null);

        return;
      }

      setError(messageFor(caught));
    },
  });

  return (
    <>
      <button
        type="button"
        className={`button button--quiet${compact ? ' button--compact' : ''}`}
        onClick={() => {
          setTitle(contactName);
          setSourceId('');
          setExisting(null);
          setError(null);
          setOpen(true);
        }}
      >
        {t('crm.qualify')}
      </button>

      <Modal
        open={open}
        title={t('crm.qualify')}
        description={t('crm.qualifyHint').replace('{name}', contactName)}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="button button--quiet" onClick={() => setOpen(false)}>
              {t('confirm.cancel')}
            </button>

            {existing ? (
              <button
                type="button"
                className="button button--primary"
                onClick={() => {
                  setOpen(false);
                  navigate(`/crm/registro/leads/${existing}`);
                }}
              >
                {t('crm.openExistingLead')}
              </button>
            ) : (
              <button
                type="button"
                className="button button--primary"
                disabled={qualify.isPending}
                onClick={() => qualify.mutate()}
              >
                {qualify.isPending ? t('work.creating') : t('crm.qualifyAction')}
              </button>
            )}
          </>
        }
      >
        {existing && <p className="alert alert--warning">{t('crm.leadAlreadyOpen')}</p>}

        <label className="stack__field">
          <span>{t('crm.qualifyTitle')}</span>
          <input
            className="input"
            autoFocus
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.qualifySource')}</span>
          <select
            className="select"
            value={sourceId}
            onChange={(event) => setSourceId(event.target.value)}
          >
            <option value="">{t('crm.qualifyNoSource')}</option>
            {active.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>

          {/* An empty catalogue is a setup step, not a dead end. */}
          {!sources.isLoading && active.length === 0 && (
            <span className="muted small">
              {t('crm.qualifyNoSources')}{' '}
              <Link to="/configuracoes/cadastros">{t('crm.qualifyConfigure')}</Link>
            </span>
          )}
        </label>

        <label className="stack__field">
          <span>{t('crm.qualifyValue')}</span>
          <input
            className="input"
            inputMode="decimal"
            placeholder="0,00"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          {/* Said out loud because it is what the number is for, not a field to fill in for tidiness. */}
          <span className="muted small">{t('crm.qualifyValueHint')}</span>
        </label>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </Modal>
    </>
  );
}
